import { DatePicker, Form, Select, Skeleton, Typography } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import {
    dateRangePresets,
    disableFutureDates,
    formatDate,
} from "../../services/util";
import { DualAxisLineChart } from "../JiraDashboard/chartUtils";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
dayjs.extend(customParseFormat);

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const chartByOption = [
    { key: "year", title: "Year" },
    { key: "days", title: "Day" },
];

const LINE_COLORS = {
    added: "var(--color-success)",
    deleted: "var(--color-error)",
    merged: "var(--color-primary-hover)",
};

const labelCol = { span: 6 };
const wrapperCol = { span: 18 };

/**
 * LinesAddedChart has 3 specific series with known semantics:
 *   - Lines Added (green, left axis)
 *   - Lines Deleted (red, left axis)
 *   - Merged Requests (blue, right axis)
 * We render a custom tri-series dual-axis chart instead of the generic one.
 */
function LinesTriAxisChart({ rawData, height = 420 }) {
    if (!rawData || rawData.length < 2) return null;
    const [headers, ...rows] = rawData;
    const [xKey, ...seriesKeys] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: String(row[0]) };
        seriesKeys.forEach((k, i) => {
            obj[k] = Number(row[i + 1]) || 0;
        });
        return obj;
    });
    const AXIS =
        seriesKeys.length >= 3
            ? [0, 0, 1] // first two on left, third on right
            : seriesKeys.map(() => 0);
    const COLORS_LIST = [
        LINE_COLORS.added,
        LINE_COLORS.deleted,
        LINE_COLORS.merged,
    ];

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div
                style={{
                    background: "var(--color-bg-card)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "var(--color-text)",
                }}
            >
                <p
                    style={{
                        margin: 0,
                        fontWeight: 600,
                        marginBottom: 6,
                        color: "var(--color-text-secondary)",
                        fontSize: 11,
                    }}
                >
                    {label}
                </p>
                {payload.map((entry) => (
                    <p
                        key={entry.name}
                        style={{ margin: "2px 0", color: entry.color }}
                    >
                        {entry.name}:{" "}
                        <strong style={{ color: "var(--color-text)" }}>
                            {entry.value}
                        </strong>
                    </p>
                ))}
            </div>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={data}
                margin={{ top: 10, right: 40, left: 0, bottom: 5 }}
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-light)"
                    vertical={false}
                />
                <XAxis
                    dataKey={xKey}
                    tick={{ fill: "var(--color-text-muted)", fontSize: 11 }}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tickLine={false}
                />
                <YAxis
                    yAxisId="left"
                    tick={{ fill: "var(--color-text-muted)", fontSize: 11 }}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tickLine={false}
                    label={{
                        value: "Lines",
                        angle: -90,
                        position: "insideLeft",
                        fill: "var(--color-text-muted)",
                        fontSize: 11,
                    }}
                />
                <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: "var(--color-text-muted)", fontSize: 11 }}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tickLine={false}
                    label={{
                        value: "Merged MRs",
                        angle: 90,
                        position: "insideRight",
                        fill: "var(--color-text-muted)",
                        fontSize: 11,
                    }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={(v) => (
                        <span style={{ color: "var(--color-text-secondary)" }}>
                            {v}
                        </span>
                    )}
                />
                {seriesKeys.map((key, i) => (
                    <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        yAxisId={AXIS[i] === 1 ? "right" : "left"}
                        stroke={COLORS_LIST[i % COLORS_LIST.length]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5 }}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

function LinesAddedChart({ projects, users }) {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [yearSelected, setYearSelected] = useState(dayjs());
    const [dateRangeSelected, setDateRangeSelected] = useState([
        dayjs().subtract(7, "day"),
        dayjs(),
    ]);
    const [form] = Form.useForm();
    const [gitCommits, setGitCommits] = useState([]);
    const [gitMeta, setGitMeta] = useState(null);
    const [formData, setFormData] = useState({
        staff_id,
        range_year: yearSelected.get("year"),
        project_id: -1,
        user_id: "",
        chart_by: chartByOption[0].key,
    });
    const [loader, setLoader] = useState(true);

    const {
        status: gitCommitStatus,
        data: gitCommitRes,
        refetch: getGitCommits,
    } = useQuery(
        "gitCommitData",
        async () =>
            await HTTP("/analytics/getGitCommitsMonthlyData", { ...formData }),
        { enabled: false },
    );

    useEffect(() => {
        getGitCommits();
        form.setFieldsValue({
            chart_by: chartByOption[0].key,
            range_year: yearSelected,
        });
    }, []);

    useEffect(() => {
        setLoader(true);
        getGitCommits();
    }, [formData]);

    useEffect(() => {
        if (gitCommitStatus === "success") {
            setLoader(false);
            setGitCommits(gitCommitRes.data.data);
            setGitMeta(gitCommitRes.data);
        }
    }, [gitCommitStatus, gitCommitRes]);

    function handleYearChange(_, dateString) {
        setYearSelected(dateString === "" ? dayjs() : dayjs(dateString));
        setFormData((prev) => ({
            ...prev,
            range_year: dateString || dayjs().get("year"),
        }));
    }

    function handleProjectSelectionChange(e) {
        setFormData((prev) => ({
            ...prev,
            project_id: e ? e.split("#")[0] : -1,
        }));
    }

    function handleUserSelectionChange(user_id) {
        setFormData((prev) => ({ ...prev, user_id }));
    }

    function handleChartBySelection(chart_by) {
        const updated = { ...formData, chart_by };
        if (chart_by === "year") {
            updated.range_year = yearSelected.get("year");
            delete updated.start_date;
            delete updated.end_date;
            form.setFieldsValue({ range_year: yearSelected });
        } else {
            delete updated.range_year;
            const [s, e] = dateRangeSelected;
            updated.start_date = formatDate(s);
            updated.end_date = formatDate(e);
            form.setFieldsValue({ range_date: dateRangeSelected });
        }
        setFormData(updated);
    }

    function handleDateRangeChange(e) {
        const start =
            dayjs(e[0]) < dayjs()
                ? dayjs(e[0]).startOf("day")
                : dayjs().startOf("day");
        const end =
            dayjs(e[1]) < dayjs()
                ? dayjs(e[1]).startOf("day")
                : dayjs().startOf("day");
        setDateRangeSelected([start, end]);
        form.setFieldsValue({ range_date: [start, end] });
        setFormData((prev) => ({
            ...prev,
            start_date: formatDate(start),
            end_date: formatDate(end),
        }));
    }

    function disabledDate(current) {
        return (
            (current && current < dayjs().subtract(90, "day")) ||
            current > dayjs().endOf("day")
        );
    }

    const isReady = !loader && gitCommits.length > 0;

    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
                marginBottom: 20,
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        width: 3,
                        height: 18,
                        borderRadius: 2,
                        background: "var(--gradient-primary)",
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 600,
                        fontSize: 15,
                    }}
                >
                    Lines Changed
                </span>
            </div>

            {/* Legend pills */}
            <div
                style={{
                    display: "flex",
                    gap: 16,
                    marginBottom: 16,
                    flexWrap: "wrap",
                }}
            >
                {[
                    {
                        color: LINE_COLORS.added,
                        label: "Lines Added",
                        axis: "Left axis",
                    },
                    {
                        color: LINE_COLORS.deleted,
                        label: "Lines Deleted",
                        axis: "Left axis",
                    },
                    {
                        color: LINE_COLORS.merged,
                        label: "Merged Requests",
                        axis: "Right axis",
                    },
                ].map(({ color, label, axis }) => (
                    <div
                        key={label}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        <div
                            style={{
                                width: 24,
                                height: 3,
                                borderRadius: 2,
                                background: color,
                            }}
                        />
                        <span
                            style={{
                                fontSize: 12,
                                color: "var(--color-text-muted)",
                            }}
                        >
                            {label}
                        </span>
                        <span
                            style={{
                                fontSize: 10,
                                color: "var(--color-text-muted)",
                                background: "var(--color-bg-control)",
                                padding: "1px 6px",
                                borderRadius: 4,
                                border: "1px solid var(--color-border)",
                            }}
                        >
                            {axis}
                        </span>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div
                style={{
                    background: "var(--color-bg-control)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "16px 20px",
                    marginBottom: 20,
                }}
            >
                <Form
                    form={form}
                    labelCol={labelCol}
                    wrapperCol={wrapperCol}
                    size="small"
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "0 24px",
                        }}
                    >
                        <Form.Item
                            label={
                                <span
                                    style={{ color: "var(--color-text-muted)" }}
                                >
                                    Project
                                </span>
                            }
                            name="project_id"
                        >
                            <Select
                                allowClear
                                showSearch
                                placeholder="All Projects"
                                loading={!projects.length}
                                onChange={handleProjectSelectionChange}
                            >
                                {projects.map((e) => (
                                    <Option
                                        key={`${e.project_id}#${e.project_name}`}
                                    >
                                        {e.project_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label={
                                <span
                                    style={{ color: "var(--color-text-muted)" }}
                                >
                                    User
                                </span>
                            }
                            name="user_id"
                        >
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="All Users"
                                loading={!users.length}
                                onChange={handleUserSelectionChange}
                            >
                                {users.map((e) => (
                                    <Option key={e.committer_email}>
                                        {e.committer_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label={
                                <span
                                    style={{ color: "var(--color-text-muted)" }}
                                >
                                    Chart By
                                </span>
                            }
                            name="chart_by"
                        >
                            <Select onChange={handleChartBySelection}>
                                {chartByOption.map((e) => (
                                    <Option key={e.key} value={e.key}>
                                        {e.title}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            shouldUpdate={(prev, cur) =>
                                prev.chart_by !== cur.chart_by
                            }
                            noStyle
                        >
                            {({ getFieldValue }) =>
                                getFieldValue("chart_by") === "year" ? (
                                    <Form.Item
                                        label={
                                            <span
                                                style={{
                                                    color: "var(--color-text-muted)",
                                                }}
                                            >
                                                Year
                                            </span>
                                        }
                                        name="range_year"
                                    >
                                        <DatePicker
                                            onChange={handleYearChange}
                                            picker="year"
                                            allowClear={false}
                                            disabledDate={disableFutureDates}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                ) : (
                                    <Form.Item
                                        label={
                                            <span
                                                style={{
                                                    color: "var(--color-text-muted)",
                                                }}
                                            >
                                                Date Range
                                            </span>
                                        }
                                        name="range_date"
                                    >
                                        <RangePicker
                                            disabledDate={disabledDate}
                                            onChange={handleDateRangeChange}
                                            format="MM/DD/YYYY"
                                            allowClear={false}
                                            style={{ width: "100%" }}
                                            presets={dateRangePresets.filter(
                                                (preset) =>
                                                    preset.label == "Today" ||
                                                    preset.label !==
                                                        "Current Month" ||
                                                    preset.label !==
                                                        "Last Month",
                                            )}
                                        />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                    </div>
                </Form>
            </div>

            {/* Chart */}
            {isReady ? (
                <>
                    <LinesTriAxisChart rawData={gitCommits} height={420} />

                    {/* Summary stats (day view only) */}
                    {formData.chart_by === "days" && gitMeta && (
                        <div
                            style={{
                                display: "flex",
                                gap: 16,
                                marginTop: 20,
                                justifyContent: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            {[
                                {
                                    label: "Committed Lines",
                                    value: gitMeta.commit_lines,
                                    color: LINE_COLORS.added,
                                },
                                {
                                    label: "Deleted Lines",
                                    value: gitMeta.deleted_lines,
                                    color: LINE_COLORS.deleted,
                                },
                            ].map(({ label, value, color }) => (
                                <div
                                    key={label}
                                    style={{
                                        background: "var(--color-bg-control)",
                                        border: `1px solid ${color}33`,
                                        borderRadius: 10,
                                        padding: "12px 24px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 4,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 22,
                                            fontWeight: 700,
                                            color,
                                        }}
                                    >
                                        {value ?? "—"}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            color: "var(--color-text-muted)",
                                        }}
                                    >
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 8, width: "100%" }}
                />
            )}
        </div>
    );
}

export default LinesAddedChart;
